import { Meta } from '@/src/components/common/Meta';
import { JAM_EVENT_DETAILS_ROUTES } from '@/src/routes';
import { useFlashbars } from '@/src/store/flashbar.context';
import { useJamChallengeDetails } from '@/src/store/jam-challenge-details.context';
import { useJamChallenge } from '@/src/store/jam-challenge.context';
import { useJamEventDetails } from '@/src/store/jam-event-details.context';
import { i18nKeys } from '@/src/utils/i18n.utils';
import { LoggingService } from '@/src/utils/logging-service.utils';
import { Box, ContentLayout, Header, SpaceBetween } from '@amzn/awsui-components-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useHistory } from 'react-router-dom';
import { LoadingBar } from '../../common/LoadingBar';
import JamChallengeOverview from '../../ui/molecules/MyJams/JamChallengeDetails/JamOverview/JamChallengeOverview';
import TaskDetails from '../../ui/molecules/MyJams/JamChallengeDetails/JamOverview/TaskDetails';
import JamArenaPanel from '../../ui/organisms/MyJams/JamArena/JamArenaPanel';
import JamChallengeHeader from '../JamChallengeHeader/JamChallengeHeader';
import './JamChallengeDetails.scss';
import { config } from '@/src/config/app-config';

const JamChallengeDetails: React.FC = () => {
  const { t } = useTranslation();
  const { eventName, event } = useJamEventDetails();
  const params: { 0: string; 1: string } = useParams();
  const { eventFeedbackData, loadJamChallengeFeedbackData, isFetchingJamChallengeFeedback } = useJamChallenge();
  const {
    loadJamChallengeDetails,
    challengeProgress,
    selectedJamChallenge,
    loadChallengeSupportDetails,
    loadJamChallengeQueueInfo,
    isSelectedChallengeStarted,
  } = useJamChallengeDetails();
  const { clearFlashbars } = useFlashbars();
  const [isLoading, setIsLoading] = useState(true);
  const history = useHistory();

  const isFacilitator = !!event?.facilitator;
  const isLocked = !!selectedJamChallenge?.locked;
  // SCENARIO challenges are judged via Arena; their placeholder task is never shown.
  const isArenaChallenge = config.ff.enableArena && !!selectedJamChallenge?.arena;
  useEffect(() => {
    const initialize = async () => {
      try {
        clearFlashbars();
        setIsLoading(true);

        // load challenge only for empty selectedChallenge or new request
        if (!selectedJamChallenge || selectedJamChallenge?.id !== params[1]) {
          await loadJamChallengeDetails({ id: eventName, challengeId: params[1] });
          void loadJamChallengeFeedbackData({ id: eventName });
        }

        // if user is facilitator load clues
        if (isFacilitator) {
          void loadChallengeSupportDetails(eventName, params[1]);
        }
      } catch (e) {
        LoggingService.debug('Error :', e);
      }
      setIsLoading(false);
    };

    void initialize();
  }, [eventName, params[1]]);

  useEffect(() => {
    if (!isLocked && isSelectedChallengeStarted) {
      void loadJamChallengeQueueInfo(eventName, params[1]);
    }
  }, [isLocked, isSelectedChallengeStarted]);

  const getChallengeFeedbackUrl = (challengeId: string) => {
    return `${JAM_EVENT_DETAILS_ROUTES.Feedback.resolve(eventName)}?challengeId=${challengeId}`;
  };

  useEffect(() => {
    // wait for feedback data to load before deciding whether to redirect
    if (isFetchingJamChallengeFeedback || eventFeedbackData === undefined) {
      return;
    }

    // if challenge is completed and no feedback found, then redirect to feedback page
    if (
      challengeProgress?.challengeId === params[1] &&
      challengeProgress?.completed &&
      (!eventFeedbackData?.challengeFeedback || !Object.keys(eventFeedbackData.challengeFeedback).includes(params[1]))
    ) {
      const feedbackUrl = getChallengeFeedbackUrl(challengeProgress.challengeId);
      history.push(feedbackUrl);
    }
  }, [challengeProgress, eventFeedbackData, isFetchingJamChallengeFeedback]);

  const challengeTitle = selectedJamChallenge?.title ?? t(i18nKeys.meta.challenge.title);
  const eventTitle = event?.title ?? t(i18nKeys.meta.event.title);
  const pageTitle = `${challengeTitle} | ${eventTitle}`;

  if (isLoading) {
    return (
      <>
        <Meta title={pageTitle} />
        <LoadingBar />
      </>
    );
  }

  if (!selectedJamChallenge) {
    return (
      <>
        <Meta title={pageTitle} />
        <JamChallengeHeader hideProperties hideHeader />
      </>
    );
  }

  return (
    <>
      <Meta title={pageTitle} />
      <ContentLayout
        header={
          <Header variant="h1" description={t(i18nKeys.myJams.challenges.subtitle)}>
            {selectedJamChallenge?.title.length > 50
              ? `${selectedJamChallenge.title.slice(0, 50)}...`
              : selectedJamChallenge?.title}
          </Header>
        }
      >
        <SpaceBetween direction="vertical" size="xxl">
          <JamChallengeHeader hideProperties />
          {selectedJamChallenge?.learningOutcome?.summary && (
            <Box>
              <Box variant="h1" margin={{ bottom: 'm' }}>
                {t(i18nKeys.myJams.challenges.details.overview.title)}
              </Box>
              <JamChallengeOverview />
            </Box>
          )}
          {isArenaChallenge ? (
            <JamArenaPanel />
          ) : (
            <Box>
              <Box variant="h2">{t(i18nKeys.myJams.challenges.details.overview.challengeProgress)}</Box>
              <Box variant="p" margin={{ bottom: 'm' }}>
                {t(i18nKeys.myJams.challenges.details.overview.selectTaskToBeginChallenge)}
              </Box>
              <TaskDetails />
            </Box>
          )}
        </SpaceBetween>
      </ContentLayout>
    </>
  );
};

export default JamChallengeDetails;
